import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, adminApi } from '@wa/shared';

/**
 * Connecting a number the client is still answering on their phone.
 *
 * Meta calls this coexistence: the WhatsApp Business app keeps working on the
 * handset, and the same number also becomes a Cloud API number here. It cannot be
 * set up the way an ordinary channel is, because there is no access token to paste —
 * the client signs in to their own Facebook account inside Meta's dialog, taps
 * "Connect to the Business Platform" in the app on their phone, and types the code
 * Meta messages them. What comes back to this page is a one-time code, and it is
 * worth <b>about thirty seconds</b>.
 *
 * That single fact shapes the whole component:
 *
 *  - Everything the server needs beyond the code — which client, which Meta app, how
 *    much history — is collected <i>before</i> the popup opens. A confirmation screen
 *    after it closes would spend the code, and would do it invisibly: on a fast
 *    connection during testing it works every time.
 *  - The POST is fired from inside Meta's own callback, with nothing between.
 *  - The two halves of the answer arrive by different routes — the code through the
 *    `FB.login` callback, the account and number ids through a `postMessage` — so the
 *    message is stashed in a ref as it lands and read when the callback fires.
 *
 * The origin check on that listener is not a formality. Without it any page the
 * operator has open could post a WhatsApp account id of its choosing and have this
 * console attach a stranger's number to a client.
 */

type SignupIds = { wabaId: string; phoneNumberId: string };

interface FacebookSdk {
  init(options: { appId: string; cookie?: boolean; xfbml?: boolean; version: string }): void;
  login(
    callback: (response: { authResponse?: { code?: string } | null; status?: string }) => void,
    options: Record<string, unknown>,
  ): void;
}

declare global {
  interface Window { FB?: FacebookSdk; fbAsyncInit?: () => void }
}

/** Meta's dialog posts from these, and nothing else may be believed. */
const META_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com'];

/**
 * Loads Meta's SDK once and initialises it for the chosen app.
 *
 * `FB.init` binds one app id, so switching Meta apps mid-session re-initialises
 * rather than reusing — cheap, and the alternative is a popup that silently belongs
 * to the previously selected client.
 */
let sdkLoading: Promise<void> | null = null;

function loadSdk(appId: string, graphVersion: string): Promise<FacebookSdk> {
  sdkLoading ??= new Promise<void>((resolve, reject) => {
    if (window.FB) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => { sdkLoading = null; reject(new Error('Meta\'s SDK would not load. Check the network.')); };
    document.head.appendChild(script);
  });

  return sdkLoading.then(() => {
    const fb = window.FB;
    if (!fb) throw new Error('Meta\'s SDK loaded but did not start.');
    fb.init({ appId, cookie: true, xfbml: false, version: graphVersion });
    return fb;
  });
}

export function ConnectCoexistence({ onClose, onConnected }: {
  onClose: () => void;
  onConnected: (summary: string) => void;
}) {
  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: adminApi.tenants });
  const metaApps = useQuery({ queryKey: ['admin', 'meta-apps'], queryFn: adminApi.metaApps });

  const [tenantId, setTenantId] = useState<number | null>(null);
  const [metaAppId, setMetaAppId] = useState<number | null>(null);
  const [historyMonths, setHistoryMonths] = useState(6);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ids = useRef<SignupIds | null>(null);
  const refused = useRef<string | null>(null);

  const config = useQuery({
    queryKey: ['admin', 'signup-config', metaAppId],
    queryFn: () => adminApi.signupConfig(metaAppId!),
    enabled: metaAppId !== null,
  });

  // Warm the SDK as soon as an app is chosen, so the click that opens the popup is
  // not also a script download. Nothing here is on the code's clock yet.
  useEffect(() => {
    if (config.data?.configId) {
      void loadSdk(config.data.metaAppId, config.data.graphVersion).catch(() => {});
    }
  }, [config.data]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!META_ORIGINS.includes(event.origin)) return;

      let payload: { type?: string; event?: string; data?: Record<string, string> };
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (payload.event === 'FINISH' || payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        const waba = payload.data?.waba_id;
        const phone = payload.data?.phone_number_id;
        if (waba && phone) { ids.current = { wabaId: waba, phoneNumberId: phone }; refused.current = null; }
        return;
      }

      // CANCEL carries the step they stopped on, which is the single most useful
      // thing to put in front of whoever is on the phone with the client.
      refused.current = payload.event === 'CANCEL'
        ? `The client closed Meta's dialog at "${payload.data?.current_step ?? 'an early step'}".`
        : `Meta reported an error: ${payload.data?.error_message ?? 'no reason given'}.`;
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const connect = useMutation({
    mutationFn: adminApi.connectCoexistence,
    onSuccess: (result) => {
      setBusy(false);
      const number = result.displayPhoneNumber ?? result.verifiedName ?? 'the number';
      onConnected(
        result.verified
          ? `${number} is connected and live at ${result.sendRatePerSecond}/s.`
            + (result.historyMonths > 0
              ? ` ${result.historyMonths} months of history was requested — it arrives in chunks over the next while.`
              : ' No history was requested.')
            + (result.subscribeProblem ? ` Webhooks: ${result.subscribeProblem}` : '')
          : `${number} was saved but Meta refused it: ${result.problem ?? 'no reason given'}. It is inactive until that is fixed.`,
      );
    },
    onError: (e) => {
      setBusy(false);
      setError(e instanceof ApiError ? e.message : 'The connection could not be completed.');
    },
  });

  const ready = tenantId !== null && metaAppId !== null && config.data?.configId;

  const launch = async () => {
    setError(null);
    ids.current = null;
    refused.current = null;

    if (!config.data?.configId || tenantId === null || metaAppId === null) return;

    let fb: FacebookSdk;
    try {
      fb = await loadSdk(config.data.metaAppId, config.data.graphVersion);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Meta\'s SDK would not load.');
      return;
    }

    setBusy(true);

    // From here to the POST there is nothing but Meta's own dialog. Do not add a
    // step, a toast, or a confirmation: the code is expiring.
    fb.login(
      (response) => {
        const code = response?.authResponse?.code;

        if (!code) {
          setBusy(false);
          setError(refused.current ?? 'The client did not finish Meta\'s dialog, so nothing was connected.');
          return;
        }

        if (!ids.current) {
          setBusy(false);
          setError(
            'Meta returned a code but never told us which account and number it was for. '
            + 'Nothing was saved. Run it again — if it repeats, the Login configuration is '
            + 'missing the WhatsApp Business Account permission.',
          );
          return;
        }

        connect.mutate({
          tenantId,
          metaAppId,
          code,
          wabaId: ids.current.wabaId,
          phoneNumberId: ids.current.phoneNumberId,
          historyMonths,
        });
      },
      {
        config_id: config.data.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          // The feature that keeps the phone working. Without it Meta migrates the
          // number away from the WhatsApp Business app instead of sharing it.
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Connect a number the client still answers on their phone"
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onClose(); }}
    >
      <div className="w-full max-w-lg rounded border border-(--color-line) bg-(--color-surface) p-5 shadow-lg">
        <h2 className="text-body font-semibold">Connect a phone the client still uses</h2>
        <p className="mt-1 text-small text-(--color-muted)">
          The WhatsApp Business app keeps working. Replies typed on the handset appear
          here as sent messages, and the number sends at 20 a second — Meta&rsquo;s ceiling
          for a shared number, whatever tier the account is on.
        </p>

        {error && (
          <p role="alert" className="mt-3 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        <div className="mt-4 flex flex-col gap-3">
          <Field label="Client">
            <select
              value={tenantId ?? ''}
              disabled={busy}
              onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded border border-(--color-line) bg-(--color-bg) px-2 py-1.5 text-small"
            >
              <option value="">Choose a client…</option>
              {tenants.data?.map((t) => <option key={t.id} value={t.id}>{t.companyName}</option>)}
            </select>
          </Field>

          <Field label="Meta app">
            <select
              value={metaAppId ?? ''}
              disabled={busy}
              onChange={(e) => setMetaAppId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded border border-(--color-line) bg-(--color-bg) px-2 py-1.5 text-small"
            >
              <option value="">Choose an app…</option>
              {metaApps.data?.filter((a) => a.isActive).map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {metaAppId !== null && config.data && !config.data.configId && (
              <p className="mt-1 text-micro text-(--color-warn)">
                This app has no Login-for-Business configuration id, so Meta&rsquo;s dialog
                cannot be opened for it. Add one in the Meta app settings first.
              </p>
            )}
            {metaAppId !== null && config.data?.hasAppSecret === 0 && (
              <p className="mt-1 text-micro text-(--color-warn)">
                This app has no app secret stored. The code Meta returns cannot be
                exchanged without it.
              </p>
            )}
          </Field>

          <Field label="History to bring across">
            <select
              value={historyMonths}
              disabled={busy}
              onChange={(e) => setHistoryMonths(Number(e.target.value))}
              className="w-full rounded border border-(--color-line) bg-(--color-bg) px-2 py-1.5 text-small"
            >
              <option value={6}>6 months — everything Meta will give</option>
              <option value={3}>3 months</option>
              <option value={1}>1 month</option>
              <option value={0}>None</option>
            </select>
            <p className="mt-1 text-micro text-(--color-muted)">
              Past chats arrive already closed and are marked as imported, so they do
              not show as unread, do not open a 24-hour window, and are left out of
              response-time reports. The client consents to this inside Meta&rsquo;s dialog;
              choosing less here asks for less.
            </p>
          </Field>
        </div>

        <p className="mt-4 rounded border border-(--color-line) bg-(--color-bg) px-3 py-2 text-micro leading-relaxed text-(--color-muted)">
          <b className="font-semibold text-(--color-ink)">Have the client at their phone.</b>{' '}
          Meta&rsquo;s dialog asks them to sign in, open <i>Settings → Business tools → Connect
          to the Business Platform</i> in the WhatsApp Business app, and type a code Meta
          messages them. The whole thing must finish in one go — closing the dialog part
          way connects nothing.
        </p>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-(--color-line) px-3 py-1.5 text-small disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void launch()}
            disabled={!ready || busy}
            className="rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Waiting for Meta…' : 'Open Meta’s dialog'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-small font-medium">{label}</span>
      {children}
    </label>
  );
}
